import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText';
import { ALL_LENSES } from '@constants/analysisLenses';
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { useMemo } from 'react';

export default function AnalysisReport({ item }) {
  // Parse results safely
  const parsedResults = useMemo(() => {
    try {
      return typeof item.results === 'string' ? JSON.parse(item.results) : item.results;
    } catch (err) {
      console.error('[AnalysisReport] parse error', err);
      return {};
    }
  }, [item.results]);

  // Render value based on type
  const renderValue = (value, level = 1) => {
    if (value == null) return null;
    if (typeof value === 'string' || typeof value === 'number')
      return <Typography>{value}</Typography>;
    if (Array.isArray(value)) {
      return value.every((v) => typeof v !== 'object') ? (
        <ul>
          {value.map((v, i) => (
            <li key={i}>{v}</li>
          ))}
        </ul>
      ) : (
        value.map((v, i) => (
          <Box key={i} sx={{ ml: level * 2 }}>
            {renderObject(v, level + 1)}
          </Box>
        ))
      );
    }
    if (typeof value === 'object') return renderObject(value, level + 1);
    return null;
  };

  // Render object recursively
  const renderObject = (obj, level = 1) =>
    Object.entries(obj).map(([key, val]) => (
      <Box key={key} sx={{ mt: 2, ml: level * 2 }}>
        <Typography variant={`h${Math.min(level + 2, 6)}`}>{key.replace(/_/g, ' ')}</Typography>
        {renderValue(val, level)}
      </Box>
    ));

  const {
    study_id,
    analysis_item_id,
    qualitative_items_included = [],
    customer_id,
    created_at,
    analysis_lens_type,
  } = item;

  return (
    <Box sx={{ overflowY: 'auto', maxHeight: '100%', whiteSpace: 'pre-wrap' }}>
      {/* Display metadata */}
      <Box sx={{ mb: 2 }}>
        <Typography>Analysis Item ID: {analysis_item_id}</Typography>
        <Typography>Study ID: {study_id}</Typography>
        <Typography component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          Lens Type: {ALL_LENSES[analysis_lens_type] || analysis_lens_type}
          <AnalysisLensHelperText lensName={analysis_lens_type} level="overall" size="small" />
        </Typography>
        {customer_id && <Typography>Customer ID: {customer_id}</Typography>}
        {created_at && <Typography>Created: {new Date(created_at).toLocaleString()}</Typography>}
        <details>
          <summary>Qualitative Items Included ({qualitative_items_included.length})</summary>
          <ul>
            {qualitative_items_included.map((id) => (
              <li key={id}>
                <MuiLink
                  href={`/interview/${study_id}/transcripts/${id}`}
                  target="_blank"
                  rel="noopener"
                >
                  {id}
                </MuiLink>
              </li>
            ))}
          </ul>
        </details>
      </Box>
      {/* Render parsed results */}
      {Object.keys(parsedResults).length > 0 ? (
        renderObject(parsedResults)
      ) : (
        <Typography>No analysis results to display.</Typography>
      )}
    </Box>
  );
}
